import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	InputAdornment,
	MenuItem,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getDrinksConversationsStatistics } from "../../../../services/Admin/ConversationsApi";
import { useAuth } from "../../../../Context/AuthContext";

const statusLabels = {
	asking: "قيد الأسئلة",
	finished: "مكتملة",
	canceled: "ملغاة",
};

const statusColors = {
	asking: "warning",
	finished: "success",
	canceled: "error",
};

const roleLabels = {
	system: "System",
	assistant: "Agent",
	user: "العميل",
};

const roleColors = {
	system: "default",
	assistant: "primary",
	user: "success",
};

const customerApprovalLabels = {
	0: "مشروب تم رفضه من العميل",
	1: "تم قبوله من العميل",
};

const customerApprovalColors = {
	0: "error",
	1: "success",
};

const summaryCardStyles = [
	"linear-gradient(135deg, #1f6feb 0%, #43a4ff 100%)",
	"linear-gradient(135deg, #0f9d58 0%, #56d47e 100%)",
	"linear-gradient(135deg, #f59e0b 0%, #facc15 100%)",
	"linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)",
];

const normalizeAssistantPayload = (value) => {
	if (typeof value !== "string") return null;

	const trimmedValue = value.trim();
	if (!trimmedValue.startsWith("{")) return null;

	const cleanedValue = trimmedValue.replace(/\\([^"\\/bfnrtu])/g, "$1");

	try {
		return JSON.parse(cleanedValue);
	} catch {
		return null;
	}
};

const getAssistantMessageOnly = (payload) => {
	const rawMessage = payload?.message;

	if (rawMessage === null || rawMessage === undefined) return "";

	if (typeof rawMessage === "string") {
		const trimmedMessage = rawMessage.trim();
		if (!trimmedMessage) return "";

		const nestedPayload = normalizeAssistantPayload(trimmedMessage);
		if (nestedPayload?.message) return String(nestedPayload.message).trim();

		return trimmedMessage;
	}

	if (typeof rawMessage === "object") {
		return String(rawMessage?.message || "").trim();
	}

	return String(rawMessage).trim();
};

const getCustomerApprovalValue = (conversation, generatedDrink) => {
	const candidates = [
		conversation?.drink?.is_approved,
		conversation?.ai_drink?.is_approved,
		generatedDrink?.is_approved,
		generatedDrink?.data?.is_approved,
		generatedDrink?.drink?.is_approved,
	];

	const rawValue = candidates.find((value) => value !== null && value !== undefined && value !== "");
	const numericValue = Number(rawValue);

	return numericValue === 0 || numericValue === 1 ? numericValue : null;
};

const getDrinkMaterials = (conversation, generatedDrink) => {
	const candidates = [
		conversation?.drink?.materials,
		conversation?.ai_drink?.materials,
		generatedDrink?.materials,
		generatedDrink?.data?.materials,
		generatedDrink?.drink?.materials,
	];
	const materials = candidates.find((items) => Array.isArray(items)) || [];

	return materials
		.map((item, index) => {
			const material = item?.material || {};
			const quantity = item?.quantity ?? item?.amount ?? material?.quantity ?? material?.amount ?? "";
			const unit = item?.unit || material?.unit || "";
			const costPricePerUnit =
				item?.cost_price_per_unit ??
				item?.cost ??
				material?.cost_price_per_unit ??
				material?.cost ??
				"";

			return {
				key: item?.id || item?.material_id || material?.id || index,
				name: material?.name || item?.name || item?.material_name || "خامة غير معروفة",
				quantity,
				unit,
				costPricePerUnit,
			};
		})
		.filter((item) => item.name || item.quantity || item.costPricePerUnit);
};

const formatDateTime = (value) => {
	if (!value) return "-";

	const parsedDate = new Date(value);
	if (Number.isNaN(parsedDate.getTime())) return "-";

	return parsedDate.toLocaleString("ar-EG", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hour12: true,
	});
};

const pad2 = (value) => String(value).padStart(2, "0");

const toLocalDateTimeInputValue = (date) => {
	const nextDate = date instanceof Date ? date : new Date(date);
	if (Number.isNaN(nextDate.getTime())) return "";

	return [
		nextDate.getFullYear(),
		pad2(nextDate.getMonth() + 1),
		pad2(nextDate.getDate()),
	].join("-") + `T${pad2(nextDate.getHours())}:${pad2(nextDate.getMinutes())}`;
};

const getTodayDateTimeRange = () => {
	const start = new Date();
	start.setHours(0, 0, 0, 0);

	const end = new Date();
	end.setHours(23, 59, 59, 999);

	return {
		from: toLocalDateTimeInputValue(start),
		to: toLocalDateTimeInputValue(end),
	};
};

const getConversationDisplayData = (conversation) => {
	const messages = Array.isArray(conversation?.messages) ? conversation.messages : [];
	const userMessages = messages.filter((message) => message?.role === "user");
	const assistantMessages = messages.filter((message) => message?.role === "assistant");
	const systemMessages = messages.filter((message) => message?.role === "system");
	const finalAssistantMessage = [...assistantMessages]
		.reverse()
		.find((message) => normalizeAssistantPayload(message?.content));
	const generatedDrink = normalizeAssistantPayload(finalAssistantMessage?.content);
	const customerApprovalValue = getCustomerApprovalValue(conversation, generatedDrink);
	const drinkMaterials = getDrinkMaterials(conversation, generatedDrink);

	return {
		totalMessages: messages.length,
		userMessagesCount: userMessages.length,
		assistantMessagesCount: assistantMessages.length,
		systemMessagesCount: systemMessages.length,
		generatedDrink,
		drinkMaterials,
		customerApprovalValue,
		customerApprovalLabel: customerApprovalLabels[customerApprovalValue] || "-",
	};
};

const ConversationsAiAgentPage = () => {
	// Helper to flatten conversation data for Excel
	const flattenConversation = (conversation) => {
		const displayData = getConversationDisplayData(conversation);
		return {
			"معرف المحادثة": conversation.id,
			"حالة المحادثة": statusLabels[conversation.status] || conversation.status,
			"اسم العميل": conversation?.user?.name || "عميل غير مسجل",
			"هاتف العميل": conversation?.user?.phone || "-",
			"بريد العميل": conversation?.user?.email || "-",
			"عدد الأسئلة": conversation?.questions_count ?? 0,
			"عدد الرسائل": displayData.totalMessages,
			"رسائل العميل": displayData.userMessagesCount,
			"رسائل Agent": displayData.assistantMessagesCount,
			"تم توليد مشروب": displayData.generatedDrink?.drink_name || "-",
			"قرار العميل": displayData.customerApprovalLabel,
			"حرارة المشروب": displayData.generatedDrink?.temperature || "-",
			"طعم المشروب": displayData.generatedDrink?.taste || "-",
			"مكونات المشروب": Array.isArray(displayData.generatedDrink?.ingredients)
				? displayData.generatedDrink.ingredients.map((i) => `${i.name} (${i.amount} ${i.unit})`).join(", ")
				: "-",
			"خامات المشروب": displayData.drinkMaterials.length
				? displayData.drinkMaterials
					.map((material, index) => {
						const quantity = material.quantity ? ` - الكمية: ${material.quantity}${material.unit ? ` ${material.unit}` : ""}` : "";
						const cost = material.costPricePerUnit ? ` - تكلفة الوحدة: ${material.costPricePerUnit}` : "";
						return `${index + 1}. ${material.name}${quantity}${cost}`;
					})
					.join("\n")
				: "-",
			"تاريخ آخر تحديث": formatDateTime(conversation.updated_at),
			"تاريخ الإنشاء": formatDateTime(conversation.created_at),
		};
	};

	// Export handler
	const handleExportExcel = () => {
		const dataToExport = filteredConversations.map(flattenConversation);
		const worksheet = XLSX.utils.json_to_sheet(dataToExport);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "Conversations");
		XLSX.writeFile(workbook, `conversations_ai_agent_export_${new Date().toISOString().slice(0,10)}.xlsx`);
	};
	const [conversations, setConversations] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [searchText, setSearchText] = useState("");
	const [statusFilter, setStatusFilter] = useState("all");
	const [dateFrom, setDateFrom] = useState(() => getTodayDateTimeRange().from);
	const [dateTo, setDateTo] = useState(() => getTodayDateTimeRange().to);
	const [selectedConversation, setSelectedConversation] = useState(null);
  const { hasPermission } = useAuth();


	const fetchConversations = async () => {
		setLoading(true);
		setError("");

		try {
			const response = await getDrinksConversationsStatistics();
			setConversations(Array.isArray(response?.data) ? response.data : []);
		} catch (err) {
			console.error("خطأ في جلب تقارير محادثات المشروبات:", err);
			setError(err?.message || "حدث خطأ أثناء جلب تقارير محادثات المشروبات");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchConversations();
	}, []);

	const filteredConversations = useMemo(() => {
		const normalizedQuery = searchText.trim().toLowerCase();
		const fromMs = dateFrom ? new Date(dateFrom).getTime() : null;
		const toMs = dateTo ? new Date(dateTo).getTime() : null;

		return conversations.filter((conversation) => {
			const matchesStatus =
				statusFilter === "all" || String(conversation?.status || "") === statusFilter;

			if (!matchesStatus) return false;

			const createdAtMs = conversation?.created_at
				? new Date(conversation.created_at).getTime()
				: null;

			if (Number.isFinite(fromMs) && (!Number.isFinite(createdAtMs) || createdAtMs < fromMs)) {
				return false;
			}

			if (Number.isFinite(toMs) && (!Number.isFinite(createdAtMs) || createdAtMs > toMs)) {
				return false;
			}

			if (!normalizedQuery) return true;

			const displayData = getConversationDisplayData(conversation);
			const haystack = [
				conversation?.id,
				conversation?.status,
				statusLabels[conversation?.status],
				conversation?.questions_count,
				conversation?.user?.name,
				conversation?.user?.phone,
				conversation?.user?.email,
				conversation?.drink?.drink_name,
				displayData.generatedDrink?.drink_name,
				displayData.customerApprovalLabel,
				...(Array.isArray(conversation?.messages)
					? conversation.messages.map((message) => `${message?.role || ""} ${message?.content || ""}`)
					: []),
			]
				.map((item) => String(item ?? "").toLowerCase())
				.join(" ");

			return haystack.includes(normalizedQuery);
		});
	}, [conversations, searchText, statusFilter, dateFrom, dateTo]);

	const summaryCards = useMemo(() => {
		const finishedCount = conversations.filter((item) => item?.status === "finished").length;
		const askingCount = conversations.filter((item) => item?.status === "asking").length;
		const generatedDrinksCount = conversations.filter(
			(item) => !!getConversationDisplayData(item).generatedDrink
		).length;

		return [
			{ label: "إجمالي المحادثات", value: conversations.length },
			{ label: "المحادثات المكتملة", value: finishedCount },
			{ label: "قيد الأسئلة", value: askingCount },
			{ label: "تم توليد مشروب", value: generatedDrinksCount },
		];
	}, [conversations]);

	const selectedConversationData = useMemo(() => {
		if (!selectedConversation) return null;
		return getConversationDisplayData(selectedConversation);
	}, [selectedConversation]);

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "400px",
				}}
			>
				<CircularProgress size={60} />
			</Box>
		);
	}

	return (
		<Box sx={{ p: 3 }}>
			<Box
				sx={{
					mb: 3,
					display: "flex",
					alignItems: { xs: "flex-start", sm: "center" },
					justifyContent: "space-between",
					gap: 2,
					flexWrap: "wrap",
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
					<ForumRoundedIcon sx={{ fontSize: 40, color: "primary.main" }} />
					<Box>
						<Typography variant="h4" sx={{ fontWeight: "bold" }}>
							تقارير محادثات Agent المشروبات
						</Typography>
						<Typography variant="body2" color="text.secondary">
							متابعة تفاصيل أسئلة العميل وردود الـ agent وحالة توليد المشروبات.
						</Typography>
					</Box>
				</Box>

						<Button
							variant="outlined"
							startIcon={<RefreshIcon />}
							onClick={fetchConversations}
							disabled={loading}
						>
							تحديث البيانات
						</Button>
						{hasPermission("export") && (
						<Button
							variant="contained"
							color="success"
							sx={{ ml: 2 }}
							onClick={handleExportExcel}
							disabled={filteredConversations.length === 0}
						>
							تصدير إلى Excel
						</Button>
						)}
			</Box>

			{error && (
				<Alert severity="error" sx={{ mb: 3 }}>
					{error}
				</Alert>
			)}

			<Grid container spacing={2} sx={{ mb: 3 }}>
				{summaryCards.map((card, index) => (
					<Grid item xs={12} sm={6} md={3} key={card.label}>
						<Card sx={{ background: summaryCardStyles[index], color: "#fff" }}>
							<CardContent>
								<Typography variant="body1" sx={{ opacity: 0.9, mb: 1 }}>
									{card.label}
								</Typography>
								<Typography variant="h3" sx={{ fontWeight: "bold" }}>
									{card.value}
								</Typography>
							</CardContent>
						</Card>
					</Grid>
				))}
			</Grid>

			<Paper sx={{ p: 2, mb: 3 }}>
				<Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap" useFlexGap>
					<TextField
						fullWidth
						size="small"
						placeholder="ابحث برقم المحادثة أو حالة المحادثة أو محتوى الرسائل أو بيانات العميل"
						value={searchText}
						onChange={(event) => setSearchText(event.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" />
								</InputAdornment>
							),
						}}
						sx={{ flex: { md: "1 1 320px" } }}
					/>

					<TextField
						size="small"
						type="datetime-local"
						label="من تاريخ ووقت"
						value={dateFrom}
						onChange={(event) => setDateFrom(event.target.value)}
						InputLabelProps={{ shrink: true }}
						sx={{ minWidth: { xs: "100%", md: 230 } }}
					/>

					<TextField
						size="small"
						type="datetime-local"
						label="إلى تاريخ ووقت"
						value={dateTo}
						onChange={(event) => setDateTo(event.target.value)}
						InputLabelProps={{ shrink: true }}
						sx={{ minWidth: { xs: "100%", md: 230 } }}
					/>

					<Select
						size="small"
						value={statusFilter}
						onChange={(event) => setStatusFilter(event.target.value)}
						sx={{ minWidth: { xs: "100%", md: 200 } }}
					>
						<MenuItem value="all">كل الحالات</MenuItem>
						<MenuItem value="asking">قيد الأسئلة</MenuItem>
						<MenuItem value="finished">مكتملة</MenuItem>
						<MenuItem value="canceled">ملغاة</MenuItem>
					</Select>

					<Button
						variant="outlined"
						onClick={() => {
							const todayRange = getTodayDateTimeRange();
							setDateFrom(todayRange.from);
							setDateTo(todayRange.to);
						}}
						sx={{ minWidth: { xs: "100%", md: 120 } }}
					>
						اليوم
					</Button>
				</Stack>
			</Paper>

			{filteredConversations.length === 0 ? (
				<Paper sx={{ p: 5, textAlign: "center" }}>
					<ForumRoundedIcon sx={{ fontSize: 70, color: "text.disabled", mb: 2 }} />
					<Typography variant="h6" color="text.secondary">
						لا توجد محادثات مطابقة للبيانات الحالية.
					</Typography>
				</Paper>
			) : (
				<TableContainer component={Paper} sx={{ boxShadow: 3 }}>
					<Table dir="rtl">
						<TableHead>
							<TableRow sx={{ backgroundColor: "primary.main" }}>
								<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>#</TableCell>
								<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>الحالة</TableCell>
								<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>العميل</TableCell>
								<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>عدد الأسئلة</TableCell>
								<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>الرسائل</TableCell>
								<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>المشروب النهائي</TableCell>
								<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>قرار العميل</TableCell>
								<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>آخر تحديث</TableCell>
								<TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>التفاصيل</TableCell>
							</TableRow>
						</TableHead>

						<TableBody>
							{filteredConversations.map((conversation, index) => {
								const displayData = getConversationDisplayData(conversation);

								return (
									<TableRow
										key={conversation.id}
										hover
										sx={{
											backgroundColor:
												index % 2 === 0 ? "background.default" : "background.paper",
										}}
									>
										<TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>
											{conversation.id}
										</TableCell>

										<TableCell sx={{ textAlign: "right" }}>
											<Chip
												size="small"
												label={statusLabels[conversation?.status] || conversation?.status || "-"}
												color={statusColors[conversation?.status] || "default"}
											/>
										</TableCell>

										<TableCell sx={{ textAlign: "right" }}>
											<Box>
												<Typography variant="body2" sx={{ fontWeight: 600 }}>
													{conversation?.user?.name || "عميل غير مسجل"}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{conversation?.user?.phone || conversation?.user?.email || "لا توجد بيانات عميل"}
												</Typography>
											</Box>
										</TableCell>

										<TableCell sx={{ textAlign: "right" }}>
											{conversation?.questions_count ?? 0}
										</TableCell>

										<TableCell sx={{ textAlign: "right" }}>
											<Stack spacing={0.5}>
												<Typography variant="body2">
													الإجمالي: {displayData.totalMessages}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													عميل: {displayData.userMessagesCount} | Agent: {displayData.assistantMessagesCount}
												</Typography>
											</Stack>
										</TableCell>

										<TableCell sx={{ textAlign: "right", minWidth: 200 }}>
											{displayData.generatedDrink ? (
												<Box>
													<Typography variant="body2" sx={{ fontWeight: 700 }}>
														{displayData.generatedDrink?.drink_name || "تم توليد مشروب"}
													</Typography>
													<Typography variant="caption" color="text.secondary">
														{displayData.generatedDrink?.temperature || "-"} | {displayData.generatedDrink?.taste || "-"}
													</Typography>
												</Box>
											) : (
												<Typography variant="body2" color="text.secondary">
													لم يتم توليد مشروب بعد
												</Typography>
											)}
										</TableCell>

										<TableCell sx={{ textAlign: "right", minWidth: 170 }}>
											<Chip
												size="small"
												label={displayData.customerApprovalLabel}
												color={customerApprovalColors[displayData.customerApprovalValue] || "default"}
												variant={displayData.customerApprovalValue === null ? "outlined" : "filled"}
											/>
										</TableCell>

										<TableCell sx={{ textAlign: "right" }}>
											{formatDateTime(conversation?.updated_at)}
										</TableCell>

										<TableCell sx={{ textAlign: "right" }}>
											<Button
												variant="text"
												onClick={() => setSelectedConversation(conversation)}
											>
												عرض
											</Button>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			<Dialog
				open={!!selectedConversation}
				onClose={() => setSelectedConversation(null)}
				fullWidth
				maxWidth="md"
			>
				<DialogTitle sx={{ fontWeight: "bold" }}>
					تفاصيل المحادثة #{selectedConversation?.id}
				</DialogTitle>
				<DialogContent dividers>
					{selectedConversation && selectedConversationData && (
						<Box>
							<Grid container spacing={2} sx={{ mb: 2.5 }}>
								<Grid item xs={12} sm={6} md={3}>
									<Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
										<Typography variant="caption" color="text.secondary">
											حالة المحادثة
										</Typography>
										<Box sx={{ mt: 1 }}>
											<Chip
												size="small"
												label={statusLabels[selectedConversation?.status] || selectedConversation?.status || "-"}
												color={statusColors[selectedConversation?.status] || "default"}
											/>
										</Box>
									</Paper>
								</Grid>
								<Grid item xs={12} sm={6} md={3}>
									<Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
										<Typography variant="caption" color="text.secondary">
											قرار العميل
										</Typography>
										<Box sx={{ mt: 1 }}>
											<Chip
												size="small"
												label={selectedConversationData.customerApprovalLabel}
												color={customerApprovalColors[selectedConversationData.customerApprovalValue] || "default"}
												variant={selectedConversationData.customerApprovalValue === null ? "outlined" : "filled"}
											/>
										</Box>
									</Paper>
								</Grid>
								<Grid item xs={12} sm={6} md={3}>
									<Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
										<Typography variant="caption" color="text.secondary">
											عدد الأسئلة
										</Typography>
										<Typography variant="h6" sx={{ mt: 1, fontWeight: 700 }}>
											{selectedConversation?.questions_count ?? 0}
										</Typography>
									</Paper>
								</Grid>
								<Grid item xs={12} sm={6} md={3}>
									<Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
										<Typography variant="caption" color="text.secondary">
											بيانات العميل
										</Typography>
										<Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
											{selectedConversation?.user?.name || "عميل غير مسجل"}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{selectedConversation?.user?.phone || selectedConversation?.user?.email || "لا توجد بيانات"}
										</Typography>
									</Paper>
								</Grid>
								<Grid item xs={12} sm={6} md={3}>
									<Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
										<Typography variant="caption" color="text.secondary">
											وقت آخر تحديث
										</Typography>
										<Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
											{formatDateTime(selectedConversation?.updated_at)}
										</Typography>
									</Paper>
								</Grid>
							</Grid>

							{(selectedConversationData.generatedDrink || selectedConversationData.drinkMaterials.length > 0) && (
								<Card
									variant="outlined"
									sx={{
										mb: 3,
										borderRadius: 3,
										background:
											"linear-gradient(135deg, rgba(255,248,225,0.95), rgba(255,243,224,0.95))",
										borderColor: "warning.light",
									}}
								>
									<CardContent>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
											<AutoAwesomeIcon color="warning" />
											<Box>
												<Typography variant="caption" color="text.secondary">
													المشروب النهائي الناتج من المحادثة
												</Typography>
												<Typography variant="h6" sx={{ fontWeight: 700 }}>
													{selectedConversationData.generatedDrink?.drink_name ||
														selectedConversation?.drink?.drink_name ||
														selectedConversation?.drink?.name ||
														"مشروب مقترح"}
												</Typography>
											</Box>
										</Box>

										<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
											{selectedConversationData.generatedDrink?.temperature && (
												<Chip
													label={`الحرارة: ${selectedConversationData.generatedDrink.temperature}`}
													color="error"
													variant="outlined"
												/>
											)}
											{selectedConversationData.generatedDrink?.taste && (
												<Chip
													label={`الطعم: ${selectedConversationData.generatedDrink.taste}`}
													color="success"
													variant="outlined"
												/>
											)}
										</Stack>

										<Stack spacing={1}>
											{(selectedConversationData.generatedDrink?.ingredients || []).map((ingredient, index) => (
												<Paper key={`${ingredient?.material_id || index}-${index}`} variant="outlined" sx={{ p: 1.5 }}>
													<Stack direction="row" justifyContent="space-between" spacing={2}>
														<Typography variant="body2" sx={{ fontWeight: 600 }}>
															{ingredient?.name || "خامة غير معروفة"}
														</Typography>
														<Typography variant="body2" color="text.secondary">
															{ingredient?.amount ?? 0} {ingredient?.unit || ""}
														</Typography>
													</Stack>
												</Paper>
											))}
										</Stack>

										{selectedConversationData.drinkMaterials.length > 0 && (
											<Box sx={{ mt: 2.5 }}>
												<Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.25 }}>
													خامات المشروب
												</Typography>
												<Stack spacing={1}>
													{selectedConversationData.drinkMaterials.map((material, index) => (
														<Paper
															key={`${material.key}-${index}`}
															variant="outlined"
															sx={{ p: 1.5, borderRadius: 2, bgcolor: "background.paper" }}
														>
															<Stack
																direction={{ xs: "column", sm: "row" }}
																justifyContent="space-between"
																alignItems={{ xs: "flex-start", sm: "center" }}
																spacing={1}
															>
																<Typography variant="body2" sx={{ fontWeight: 700 }}>
																	{index + 1}. {material.name}
																</Typography>
																<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
																	<Chip
																		size="small"
																		label={`الكمية: ${material.quantity || "-"}${material.unit ? ` ${material.unit}` : ""}`}
																		variant="outlined"
																	/>
																	<Chip
																		size="small"
																		label={`تكلفة الوحدة: ${material.costPricePerUnit || "-"}`}
																		color="warning"
																		variant="outlined"
																	/>
																</Stack>
															</Stack>
														</Paper>
													))}
												</Stack>
											</Box>
										)}
									</CardContent>
								</Card>
							)}

							<Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
								تسلسل الرسائل
							</Typography>

							{selectedConversation?.messages?.length ? (
								<Stack spacing={1.5}>
									{selectedConversation.messages.map((message) => {
										const parsedPayload =
											message?.role === "assistant"
												? normalizeAssistantPayload(message?.content)
												: null;
										const assistantMessage = parsedPayload
											? getAssistantMessageOnly(parsedPayload)
											: "";

										return (
											<Paper key={message.id} variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
												<Stack
													direction={{ xs: "column", sm: "row" }}
													justifyContent="space-between"
													alignItems={{ xs: "flex-start", sm: "center" }}
													spacing={1}
													sx={{ mb: 1.25 }}
												>
													<Stack direction="row" spacing={1} alignItems="center">
														<Chip
															size="small"
															icon={
																message?.role === "user" ? <PersonIcon /> : message?.role === "assistant" ? <SmartToyIcon /> : <InfoOutlinedIcon />
															}
															label={roleLabels[message?.role] || message?.role || "-"}
															color={roleColors[message?.role] || "default"}
														/>
														<Typography variant="caption" color="text.secondary">
															{formatDateTime(message?.created_at)}
														</Typography>
													</Stack>
													<Typography variant="caption" color="text.secondary">
														Message ID: {message?.id}
													</Typography>
												</Stack>

												{parsedPayload ? (
													<Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.9 }}>
														{assistantMessage || "-"}
													</Typography>
												) : (
													<Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
														{message?.content || "-"}
													</Typography>
												)}
											</Paper>
										);
									})}
								</Stack>
							) : (
								<Paper
									variant="outlined"
									sx={{
										p: 4,
										textAlign: "center",
										color: "text.secondary",
										borderStyle: "dashed",
									}}
								>
									لا توجد رسائل محفوظة داخل هذه المحادثة.
								</Paper>
							)}

							<Divider sx={{ my: 2.5 }} />

							<Typography variant="caption" color="text.secondary">
								تاريخ الإنشاء: {formatDateTime(selectedConversation?.created_at)}
							</Typography>
						</Box>
					)}
				</DialogContent>
			</Dialog>
		</Box>
	);
};

export default ConversationsAiAgentPage;
